import React, { useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../services/firebase';
import { getAuth } from 'firebase/auth';
import { ActivityIndicator } from 'react-native-paper';
import { CommunityProvider } from './ContextCommunity/CommunityContext';
import PostCommunity from './PostCommunity';
import {
  isLegacyCommunityPost,
  VIRTUAL_GENERAL_CHANNEL_ID,
} from '../../utils/communityChannelUtils';
import THEME from '../../config/theme';
import { useCustomTheme } from '../../contexts/useCustomTheme';

function readWebSearchParams() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return { id: undefined, channelId: undefined };
  }
  const sp = new URLSearchParams(window.location.search);
  const id = sp.get('id');
  const channelId = sp.get('channelId');
  return {
    id: id && id.trim() !== '' ? id : undefined,
    channelId: channelId && channelId.trim() !== '' ? channelId : undefined,
  };
}

export function CommunityCommentsScreen() {
  const route = useRoute();
  const auth = getAuth();
  const { customColor } = useCustomTheme();
  const [webParams, setWebParams] = useState(() => readWebSearchParams());

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
    const sync = () => setWebParams(readWebSearchParams());
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  const postId = route.params?.id ?? webParams.id;
  const paramChannelId = route.params?.channelId ?? webParams.channelId;

  const [initialChannelId, setInitialChannelId] = useState(null);

  useEffect(() => {
    if (!postId) {
      setInitialChannelId(VIRTUAL_GENERAL_CHANNEL_ID);
      return;
    }

    const trimmedParam =
      typeof paramChannelId === 'string' && paramChannelId.trim() !== ''
        ? paramChannelId.trim()
        : null;

    if (trimmedParam) {
      setInitialChannelId(trimmedParam);
      return;
    }

    if (!auth.currentUser?.uid || auth.currentUser?.isAnonymous) {
      setInitialChannelId(VIRTUAL_GENERAL_CHANNEL_ID);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(firestore, 'posts', postId));
        if (cancelled) return;
        if (!snap.exists()) {
          setInitialChannelId(VIRTUAL_GENERAL_CHANNEL_ID);
          return;
        }
        const raw = snap.data()?.channelId;
        setInitialChannelId(
          isLegacyCommunityPost({ channelId: raw })
            ? VIRTUAL_GENERAL_CHANNEL_ID
            : String(raw)
        );
      } catch {
        if (!cancelled) {
          setInitialChannelId(VIRTUAL_GENERAL_CHANNEL_ID);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [postId, paramChannelId, auth.currentUser?.uid, auth.currentUser?.isAnonymous]);

  const stablePostId = useMemo(() => postId ?? null, [postId]);

  if (initialChannelId === null) {
    return (
      <ActivityIndicator
        style={{
          flex: 1,
          backgroundColor:
            customColor && customColor.background?.auth
              ? customColor.background.auth
              : THEME.COLORS.BACKGROUND_ABOUT,
          justifyContent: 'center',
        }}
        color={
          customColor && customColor.primary
            ? customColor.primary
            : THEME.COLORS.PRIMARY_800
        }
      />
    );
  }

  return (
    <CommunityProvider
      initialSelectedChannelId={initialChannelId}
      focusPostId={stablePostId}
    >
      <PostCommunity routePostId={stablePostId} />
    </CommunityProvider>
  );
}
